import { AnalyticsDataItem } from '../types/optimise';
import { AnalyticsRecord, ExtractedRates, RateType } from '../types/tariff';

import { addMinutes, formatHHmmInUK } from './dateUtils';

/**
 * Constants to define description keys and energy usage descriptions
 */
export const DESCRIPTION_KEY = 'description';
export const EXPORT_SOLAR = 'Export excess solar generation rather than storing it.';
export const AVERAGE_COST = 'Average cost electricity.';
export const HOLD_BATTERY = 'Hold the battery state of charge to be used at peak times.';
export const CHARGE_BATTERY = 'Charge the battery when electricity is cheap.';
export const USE_BATTERY = 'Use the battery and export when electricity is expensive.';

export const RateCategories: Record<string, RateType> = {
  CHEAP: 'Cheap rate',
  DAY: 'Day rate',
  PEAK: 'Peak rate',
} as const;

/**
 * Calculate total savings by comparing battery-based savings and dumb battery savings.
 *
 * @param {Array} savings - Array of savings data objects.
 * @returns {number} - The difference between smart and dumb battery savings.
 */
export const getSavingsTotal = (savings: AnalyticsDataItem[]): number => {
  let savingsTotal = 0;
  let dumbBatteryTotal = 0;

  // Loop through savings data to calculate totals
  savings.forEach(
    (item: {
      battery_earning: number;
      battery_cost: number;
      dumb_battery_earning: number;
      dumb_battery_cost: number;
    }) => {
      savingsTotal += item.battery_earning - item.battery_cost;
      dumbBatteryTotal += item.dumb_battery_earning - item.dumb_battery_cost;
    }
  );

  // Return the difference between smart and dumb battery savings
  return savingsTotal - dumbBatteryTotal;
};

/**
 * Calculate a specific quantile from an array of numbers.
 *
 * @param {Array} arr - Array of numbers.
 * @param {number} q - Quantile to calculate (0 <= q <= 1).
 * @returns {number} - The calculated quantile value.
 */
export const getQuantile = (arr: any[], q: number): number => {
  const sorted = arr.slice().sort((a: number, b: number) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;

  // Return interpolated value or the exact base value if at the end
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  } else {
    return sorted[base];
  }
};

/**
 * Determine rate categories (Cheap, Day, Peak) based on unique rates in the analytics data.
 *
 * This function classifies import or export rates into predefined categories to facilitate
 * energy usage recommendations and tariff analysis. The classification depends on the
 * number of unique rates present in the dataset:
 *
 * - **1 Unique Rate**: All entries are classified as "Day rate".
 * - **2 Unique Rates**:
 *   - The lowest rate is classified as "Cheap rate".
 *   - The highest rate is classified as "Day rate".
 *   - Entries are categorized accordingly.
 * - **3 Unique Rates**:
 *   - The lowest rate is "Cheap rate".
 *   - The medium rate is "Day rate".
 *   - The highest rate is "Peak rate".
 * - **>3 Unique Rates**:
 *   - Rates are grouped dynamically using quantiles:
 *     - Rates below the 20th percentile are "Cheap rate".
 *     - Rates above the 80th percentile are "Peak rate".
 *     - Remaining rates are classified as "Day rate".
 *   - This ensures flexibility and adaptability for dynamic tariff structures.
 *
 * @param {Array} analyticsData - The array of analytics data containing rate information.
 * @param {string} direction - Indicates whether to classify based on "import_rate" or "export_rate".
 * @returns {Object} - An object containing categorized rates:
 *   - `cheap`: Array of entries classified as "Cheap rate".
 *   - `day`: Array of entries classified as "Day rate".
 *   - `peak`: Array of entries classified as "Peak rate".
 */
export const addRateCategoriesToRecords = (analyticsData: any[], direction: string): object => {
  const rates = analyticsData.map((record: { [x: string]: string }) => parseFloat(record[`${direction}_rate`]));
  const uniqueRates = [...new Set(rates)].sort((a, b) => a - b);

  if (uniqueRates.length === 1) {
    // Single rate: All records are "Day rate"
    analyticsData.forEach((record: { [x: string]: string }) => {
      record[`${direction}_rate_category`] = RateCategories.DAY;
      record[DESCRIPTION_KEY] = HOLD_BATTERY;
    });
  } else if (uniqueRates.length === 2) {
    // Two unique rates: classify as "Cheap" and "Day"
    const [cheapRate, peakRate] = uniqueRates;
    analyticsData.forEach((record: { [x: string]: string }) => {
      if (parseFloat(record[`${direction}_rate`]) === cheapRate) {
        record[`${direction}_rate_category`] = RateCategories.CHEAP;
        record[DESCRIPTION_KEY] = CHARGE_BATTERY;
      } else if (parseFloat(record[`${direction}_rate`]) === peakRate) {
        record[`${direction}_rate_category`] = RateCategories.DAY;
        record[DESCRIPTION_KEY] = USE_BATTERY;
      }
    });
  } else if (uniqueRates.length === 3) {
    // Three unique rates: classify as "Cheap", "Day", and "Peak"
    const [cheapRate, dayRate, peakRate] = uniqueRates;
    analyticsData.forEach((record: { [x: string]: string }) => {
      if (parseFloat(record[`${direction}_rate`]) === cheapRate) {
        record[`${direction}_rate_category`] = RateCategories.CHEAP;
        record[DESCRIPTION_KEY] = CHARGE_BATTERY;
      } else if (parseFloat(record[`${direction}_rate`]) === dayRate) {
        record[`${direction}_rate_category`] = RateCategories.DAY;
        record[DESCRIPTION_KEY] = HOLD_BATTERY;
      } else if (parseFloat(record[`${direction}_rate`]) === peakRate) {
        record[`${direction}_rate_category`] = RateCategories.PEAK;
        record[DESCRIPTION_KEY] = USE_BATTERY;
      }
    });
  } else {
    // Flexible classification: group rates using quantiles
    const cheapRateThreshold = getQuantile(rates, 0.2);
    const peakRateThreshold = getQuantile(rates, 0.8);

    analyticsData.forEach((record: { [x: string]: string }) => {
      const rate = parseFloat(record[`${direction}_rate`]);
      if (rate <= cheapRateThreshold) {
        record[`${direction}_rate_category`] = RateCategories.CHEAP;
        record[DESCRIPTION_KEY] = CHARGE_BATTERY;
      } else if (rate >= peakRateThreshold) {
        record[`${direction}_rate_category`] = RateCategories.PEAK;
        record[DESCRIPTION_KEY] = USE_BATTERY;
      } else {
        record[`${direction}_rate_category`] = RateCategories.DAY;
        record[DESCRIPTION_KEY] = HOLD_BATTERY;
      }
    });
  }

  return analyticsData;
};

/**
 * Prepare data for rendering in savings chart.
 *
 * @param {Array} analyticsData - Array of raw analytics data.
 * @returns {Array} - Processed data ready for chart rendering.
 */
export const transformAnalyticsDataForChart = (analyticsData: any[]): Array<any> =>
  analyticsData.map(
    (row: {
      start_date: number;
      solar_generation: string;
      load_consumption: string;
      import_rate: string;
      export_rate: string;
      imported: string;
      exported: string;
      alternative_import_cost: string;
      actual_import_cost: string;
      actual_export_earning: string;
    }) => ({
      ...row,
      start_date: new Date(row.start_date).toISOString(),
      solar_generation: parseFloat(row.solar_generation),
      load_consumption: parseFloat(row.load_consumption),
      import_rate: parseFloat(row.import_rate),
      export_rate: parseFloat(row.export_rate),
      imported: parseFloat(row.imported),
      exported: parseFloat(row.exported),
      alternative_import_cost: parseFloat(row.alternative_import_cost),
      actual_import_cost: -parseFloat(row.actual_import_cost),
      actual_export_earning: -parseFloat(row.actual_export_earning),
      time: formatHHmmInUK(addMinutes(new Date(row.start_date), 30)),
    })
  );

export const extractRates = (
  records: Array<{
    export_rate: number;
    export_rate_category: RateType;
    import_rate: number;
    import_rate_category: RateType;
  }>
) => {
  const rateTypes: RateType[] = ['Cheap rate', 'Day rate', 'Peak rate'];

  const result: ExtractedRates = {
    import: {
      'Cheap rate': { rates: [], minRate: null, maxRate: null },
      'Day rate': { rates: [], minRate: null, maxRate: null },
      'Peak rate': { rates: [], minRate: null, maxRate: null },
    },
    export: {
      'Cheap rate': { rates: [], minRate: null, maxRate: null },
      'Day rate': { rates: [], minRate: null, maxRate: null },
      'Peak rate': { rates: [], minRate: null, maxRate: null },
    },
  };

  // Process records
  records.forEach((record) => {
    if (!result.export || !result.import) return;

    // Handle export rates
    const exportCategory = record.export_rate_category;
    if (exportCategory && result.export[exportCategory]) {
      const exportRate = Number(record.export_rate.toFixed(1));
      result.export[exportCategory].rates.push(exportRate);
    }

    // Handle import rates
    const importCategory = record.import_rate_category;
    if (importCategory && result.import[importCategory]) {
      const importRate = Number(record.import_rate.toFixed(1));
      result.import[importCategory].rates.push(importRate);
    }
  });

  // Calculate min and max rates for each category
  rateTypes.forEach((rateType) => {
    // Handle export rates
    if (result.export?.[rateType]) {
      const rates = result.export[rateType].rates;
      if (rates.length > 0) {
        result.export[rateType].minRate = Math.min(...rates);
        result.export[rateType].maxRate = Math.max(...rates);
      }
    }

    // Handle import rates
    if (result.import?.[rateType]) {
      const rates = result.import[rateType].rates;
      if (rates.length > 0) {
        result.import[rateType].minRate = Math.min(...rates);
        result.import[rateType].maxRate = Math.max(...rates);
      }
    }
  });

  return result;
};

export const extractRatesSingleDirection = (records: AnalyticsRecord[], direction: 'import' | 'export') => {
  const rateTypes: RateType[] = ['Cheap rate', 'Day rate', 'Peak rate'];

  const result = {
    [direction]: {} as Record<
      RateType,
      {
        rates: string[];
        minRate: number | null;
        maxRate: number | null;
      }
    >,
  };

  const categoryKey = `${direction}_rate_category`;
  const rateKey = `${direction}_rate`;

  // Initialize result object for each category and type (import/export)
  rateTypes.forEach((rateType) => {
    if (!result[direction]) {
      result[direction] = {} as Record<
        RateType,
        {
          rates: string[];
          minRate: number | null;
          maxRate: number | null;
        }
      >;
    }
    result[direction][rateType] = {
      rates: [],
      minRate: null,
      maxRate: null,
    };
  });

  // Process records
  records.forEach((record) => {
    const recordCategory = record[categoryKey];
    const recordRate = record[rateKey];

    if (typeof recordCategory === 'string' && rateTypes.includes(recordCategory as RateType)) {
      try {
        const rate = Number(recordRate).toFixed(2);
        if (result[direction] && result[direction][recordCategory as RateType]) {
          result[direction][recordCategory as RateType].rates.push(rate);
        }
      } catch (e) {
        console.error(e);
      }
    }
  });

  // Calculate min and max rates for each category
  rateTypes.forEach((rateType) => {
    if (result[direction] && result[direction][rateType]) {
      const rates = result[direction][rateType].rates.map(Number);
      if (rates.length > 0) {
        result[direction][rateType].minRate = Math.min(...rates);
        result[direction][rateType].maxRate = Math.max(...rates);
      }
    }
  });

  return result;
};
